import { useCallback, useEffect, useMemo, useState } from "react";
import { isEqual } from "lodash";
import {
  Button,
  Group,
  LoadingOverlay,
  Stack,
  TextInput,
  Title,
  Breadcrumbs,
  Anchor,
  Select,
} from "@mantine/core";
import { zodResolver } from "mantine-form-zod-resolver";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useDebug } from "common/debug/hooks/useDebug";
import {
  useAddChampionshipMutation,
  useUpdateChampionshipMutation,
} from "championship/api/championship.api";
import {
  ChampionshipFormProvider,
  useChampionshipForm,
} from "championship/context/championship.context";
import { championshipSchema } from "championship/admin/components/forms/ChampionshipSchema";

import type { Championship } from "championship/types/championship.model";
import { ADMIN_PATH } from "_urls";
import {
  ADMIN_ORGANIZATION,
  ORGANIZATIONS,
  CHAMPIONSHIP_ORGANIZATION_VISIBILITY_AUTHORISED_ORGANIZATIONS,
  CHAMPIONSHIP_ORGANIZATION_VISIBILITY_AUTHORISED_ROLES,
} from "_roles";
import { useOrganization } from "common/admin/auth/hooks";
import { AccessRights } from "common/admin/auth/AccessRights";
import { DatePicker, TimeInput } from "@mantine/dates";

export function ChampionshipAdminForm({
  championship,
  editMode,
}: {
  championship?: Championship;
  editMode: boolean;
}) {
  const { isDebugMode, log } = useDebug();
  const location = useLocation();
  const navigate = useNavigate();

  const [addChampionship, addChampionshipResult] = useAddChampionshipMutation();
  const [updateChampionship, updateChampionshipResult] =
    useUpdateChampionshipMutation();
  const [isLoading, setIsLoading] = useState(false);

  const [organization] = useOrganization();
  const isDuplicate = location.pathname.endsWith("/duplicate");

  useEffect(() => {
    if (isDebugMode) {
      if (addChampionshipResult.isError) {
        log(`Add error: ${JSON.stringify(addChampionshipResult.error)}`);
      }
      if (updateChampionshipResult.isError) {
        log(`Update error: ${JSON.stringify(updateChampionshipResult.error)}`);
      }
    }
  }, [isDebugMode, addChampionshipResult, updateChampionshipResult, log]);

  const getInitialValues = useCallback(() => {
    if (championship && editMode) {
      return {
        partitionId: championship.partitionId || "",
        parentId: isDuplicate ? championship.id : "",
        name: championship.name || "",
        status: championship.status || "not started",
        type: championship.type || "common",
        maxScore: championship.maxScore || 0,
        startDate: championship.startDate || new Date().toISOString(),
        endDate: championship.endDate || new Date().toISOString(),
        createdAt: championship.createdAt || new Date().toISOString(),
        owner: {
          L1: championship.owner?.L1 || organization || "",
          L2: championship.owner?.L2 || "",
          L3: championship.owner?.L3 || "",
          L4: championship.owner?.L4 || "",
        },
      };
    }

    return {
      partitionId: "",
      parentId: "",
      name: "",
      status: "not started" as
        | "not started"
        | "ongoing"
        | "published"
        | "closed"
        | "archived",
      type: "common" as "common" | "bookmarked",
      maxScore: 0,
      startDate: new Date().toISOString(),
      endDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
      createdAt: new Date().toISOString(),
      owner: {
        L1: organization || "",
        L2: "",
        L3: "",
        L4: "",
      },
    };
  }, [championship, editMode, isDuplicate, organization]);

  const form = useChampionshipForm({
    initialValues: getInitialValues(),
    validate: zodResolver(championshipSchema),
  });

  const isDirty = useMemo(
    () => !isEqual(form.values, getInitialValues()),
    [form.values, getInitialValues]
  );

  const save = async () => {
    console.log("Saving championship", form.values);
    const result = form.validate();
    if (!result.hasErrors) {
      const transformed = form.values;
      console.log("Saving championship", transformed);
      setIsLoading(true);

      try {
        if (championship && editMode && !isDuplicate) {
          await updateChampionship({ id: championship.id, ...transformed });
        } else {
          await addChampionship(transformed).unwrap();
        }
        navigate(`../`);
      } catch (error) {
        log("Error saving championship", error);
      } finally {
        setIsLoading(false);
      }
    } else {
      log("Validation failed", result.errors);
    }
  };

  return (
    <>
      {isLoading && <LoadingOverlay visible={true} overlayOpacity={0.8} />}
      <ChampionshipFormProvider form={form}>
        <Stack>
          <div
            className="sticky top-0 z-50 pb-4"
            style={{ backgroundColor: "#E9ECEF" }}
          >
            <Title order={3} align="center" m="xs">
              {isDuplicate
                ? `Duplicating ${championship?.name}`
                : championship?.name || "New Championship"}
            </Title>
            <Breadcrumbs separator="→">
              <Anchor component={Link} to={`/${ADMIN_PATH}`}>
                Admin
              </Anchor>
              <Anchor component={Link} to={`/${ADMIN_PATH}/championships`}>
                Championships
              </Anchor>
            </Breadcrumbs>
            <Group position="right">
              <Button
                disabled={!isDirty && !isDuplicate}
                onClick={save}
                className="bg-button-admin disabled:opacity-75"
              >
                Save
              </Button>
            </Group>
          </div>

          {/* Organization & Name Row */}
          <Group className="grid grid-cols-2 gap-4">
            {organization === ADMIN_ORGANIZATION ? (
              <AccessRights
                authorizedOrganizations={
                  CHAMPIONSHIP_ORGANIZATION_VISIBILITY_AUTHORISED_ORGANIZATIONS
                }
                authorizedRoles={
                  CHAMPIONSHIP_ORGANIZATION_VISIBILITY_AUTHORISED_ROLES
                }
              >
                <Select
                  label="Owner"
                  required
                  data={ORGANIZATIONS}
                  {...form.getInputProps("owner.L1")}
                />
              </AccessRights>
            ) : (
              <div />
            )}

            <TextInput label="Name" required {...form.getInputProps("name")} />
          </Group>

          {/* Status & Type Row */}
          <Group className="grid grid-cols-2 gap-4">
            <Select
              label="Status"
              placeholder="Pick status"
              data={[
                { value: "not started", label: "Not Started" },
                { value: "ongoing", label: "Started" },
                { value: "published", label: "Published" },
                { value: "closed", label: "Ended" },
                { value: "archived", label: "Archived" },
              ]}
              {...form.getInputProps("status")}
            />

            <Select
              label="Type"
              placeholder="Pick Type"
              data={[
                { value: "common", label: "Common" },
                { value: "bookmarked", label: "Bookmarked" },
              ]}
              {...form.getInputProps("type")}
            />
          </Group>

          {/* Date & Time Row */}
          <div className="grid grid-cols-2 gap-4">
            <div className="flex gap-4">
              <DatePicker
                label="Start date"
                className="flex-[7]"
                value={new Date(form.values.startDate)}
                onChange={(date) => {
                  if (date) {
                    const currentDate = new Date(form.values.startDate);
                    currentDate.setFullYear(date.getFullYear());
                    currentDate.setMonth(date.getMonth());
                    currentDate.setDate(date.getDate());

                    form.setFieldValue("startDate", currentDate.toISOString());

                    const endDate = new Date(form.values.endDate);
                    if (currentDate >= endDate) {
                      form.setFieldError(
                        "startDate",
                        "Start date must be before end date"
                      );
                    } else {
                      form.clearFieldError("startDate");
                      form.clearFieldError("endDate");
                    }
                  }
                }}
                error={form.errors["startDate"]}
              />

              <TimeInput
                label="Time"
                className="flex-[3]"
                value={new Date(form.values.startDate)}
                onChange={(date) => {
                  if (date) {
                    const currentDate = new Date(form.values.startDate);
                    currentDate.setHours(date.getHours());
                    currentDate.setMinutes(date.getMinutes());

                    form.setFieldValue("startDate", currentDate.toISOString());

                    const endDate = new Date(form.values.endDate);
                    if (currentDate >= endDate) {
                      form.setFieldError(
                        "startDate",
                        "Start time must be before end time"
                      );
                    } else {
                      form.clearFieldError("startDate");
                      form.clearFieldError("endDate");
                    }
                  }
                }}
                error={form.errors["startDate"]}
              />
            </div>

            <div className="flex gap-4">
              <DatePicker
                label="End date"
                className="flex-[7]"
                value={new Date(form.values.endDate)}
                onChange={(date) => {
                  if (date) {
                    const currentDate = new Date(form.values.endDate);
                    currentDate.setFullYear(date.getFullYear());
                    currentDate.setMonth(date.getMonth());
                    currentDate.setDate(date.getDate());

                    form.setFieldValue("endDate", currentDate.toISOString());

                    const startDate = new Date(form.values.startDate);
                    if (currentDate <= startDate) {
                      form.setFieldError(
                        "endDate",
                        "End date must be after start date"
                      );
                    } else {
                      form.clearFieldError("startDate");
                      form.clearFieldError("endDate");
                    }
                  }
                }}
                error={form.errors["endDate"]}
              />

              <TimeInput
                label="Time"
                className="flex-[3]"
                value={new Date(form.values.endDate)}
                onChange={(date) => {
                  if (date) {
                    const currentDate = new Date(form.values.endDate);
                    currentDate.setHours(date.getHours());
                    currentDate.setMinutes(date.getMinutes());

                    form.setFieldValue("endDate", currentDate.toISOString());

                    const startDate = new Date(form.values.startDate);
                    if (currentDate <= startDate) {
                      form.setFieldError(
                        "endDate",
                        "End time must be after start time"
                      );
                    } else {
                      form.clearFieldError("startDate");
                      form.clearFieldError("endDate");
                    }
                  }
                }}
                error={form.errors["endDate"]}
              />
            </div>
          </div>
          <Group className="grid grid-cols-2 gap-4">
            <TextInput
              label="Max Score"
              required
              type="number"
              {...form.getInputProps("maxScore")}
            />
          </Group>
        </Stack>
      </ChampionshipFormProvider>
    </>
  );
}
